import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import './Services.css';

// The internal "watch-me" referral flags are NOT kept here — they would ship in
// the public JS bundle. They live in docs/consultant-watch-me-reference.md.
const MACH_CERTS = [
  {
    id: 'mach-firstwords',
    name: 'First Words',
    icon: '💬',
    typicalAge: '10–16 months',
    criterion: 'Says 3+ recognizable words used consistently and in context.',
    leadsTo: 'Speech & Language: Storytelling',
  },
  {
    id: 'mach-firststeps',
    name: 'First Steps',
    icon: '👣',
    typicalAge: '11–16 months',
    criterion: 'Takes 5+ unassisted steps in a single sequence.',
    leadsTo: 'Foundational motor readiness',
  },
  {
    id: 'mach-colornaming',
    name: 'Color Naming',
    icon: '🎨',
    typicalAge: '30–36 months',
    criterion: 'Names 5 colors correctly on sight without prompting.',
    leadsTo: 'Object Identification: Foundational',
  },
  {
    id: 'mach-shaperecognition',
    name: 'Shape Recognition',
    icon: '🔷',
    typicalAge: '30–36 months',
    criterion: 'Identifies circle, square, triangle, and star on request.',
    leadsTo: 'Object Identification: Foundational',
  },
  {
    id: 'mach-countstoten',
    name: 'Counts to 10',
    icon: '🔢',
    typicalAge: '33–42 months',
    criterion: 'Counts 1 to 10 in order with no skips or repeats.',
    leadsTo: 'Mathematics: Foundational',
  },
  {
    id: 'mach-firstsentence',
    name: 'First Sentence',
    icon: '🗨️',
    typicalAge: '22–30 months',
    criterion: 'Spontaneously uses a 3+ word sentence to express a complete idea.',
    leadsTo: 'Speech & Language: Storytelling',
  },
];

const MACH_BASE_PRICE = 70;
const MACH_ADDITIONAL_PRICE = 20;

const CERTIFICATE_GROUPS = [
  {
    id: 'object-identification',
    name: 'Object Identification Certificate',
    description:
      "Looks at how a child recognizes, names, and reasons about objects across three progressive levels.",
    tiers: [
      {
        id: 'obj-foundational',
        icon: '🔵',
        name: 'Foundational',
        subtitle: 'Object Identification: Level 1',
        price: 129,
        popular: false,
        color: 'tier-green',
        includes: [
          '45-minute SKIES consultant session',
          'Milestone skills report',
          'Official Stratos Standard certificate (digital + print-ready)',
          'Achievement badge digital download',
        ],
        milestones: [
          'Names common everyday objects out loud',
          'Sorts items into basic groups (animals, food, vehicles)',
          'Points to objects when you name them',
          'Knows what everyday objects are used for',
        ],
        framework: {
          consultantTier: 'SKIES Associate',
          sources: ['Bayley-4 Cognitive (presentation discipline)', 'ASQ-3 Problem Solving (intake)', 'CDC Watch Me!'],
        },
      },
      {
        id: 'obj-advanced',
        icon: '🟧',
        name: 'Advanced',
        subtitle: 'Object Identification: Level 2',
        price: 159,
        popular: true,
        comingSoon: true,
        color: 'tier-gold',
        includes: [
          '60-minute SKIES consultant session',
          'Full cognitive skills review',
          'Premium certificate (digital + print-ready)',
          'Detailed parent milestone report',
          'Achievement badge + social media graphic',
          'One follow-up progress check call (30 min)',
        ],
        milestones: [
          'Quickly and accurately names objects one after another',
          'Connects objects that go together (for example, dog and doghouse, bird and nest)',
          'Groups objects by how they are used or how they relate',
          'Knows where objects belong',
        ],
        framework: {
          consultantTier: 'SKIES Practitioner',
          sources: ['Bayley-4 Cognitive (analogy & relational grouping)', 'ASQ-3 Problem Solving', 'CDC Watch Me!'],
        },
      },
      {
        id: 'obj-mastery',
        icon: '🌟',
        name: 'Mastery',
        subtitle: 'Object Identification: Level 3',
        price: 199,
        popular: false,
        comingSoon: true,
        color: 'tier-blue',
        includes: [
          '90-minute SKIES consultant session',
          'Advanced cognitive & perceptual skills review',
          'Premium certificate (digital + print-ready)',
          'In-depth milestone & readiness report',
          'Achievement badge + shareable digital assets',
          'One follow-up progress check call (30 min)',
        ],
        milestones: [
          'Recognizes a whole object from just a part of it',
          'Finds objects that match more than one quality (for example, blue and round)',
          'Sorts objects by color, shape, size, or texture at the same time',
          'Groups items that share a less obvious connection',
        ],
        framework: {
          consultantTier: 'SKIES Lead',
          sources: ['Bayley-4 Cognitive (multi-feature filtering, discontinue rule)', 'ASQ-3 Problem Solving', 'CDC Watch Me!'],
        },
      },
    ],
  },
  {
    id: 'math',
    name: 'Mathematics Certificate',
    description:
      "Looks at a child's early number skills and reasoning across two progressive levels.",
    tiers: [
      {
        id: 'math-foundational',
        icon: '🔢',
        name: 'Foundational',
        subtitle: 'Mathematics: Level 1',
        price: 129,
        popular: false,
        color: 'tier-green',
        includes: [
          '45-minute SKIES consultant session',
          'Milestone skills report',
          'Official Stratos Standard certificate (digital + print-ready)',
          'Achievement badge digital download',
        ],
        milestones: [
          'Instantly sees how many objects there are (1 to 5) without counting',
          'Touches or points to each item once while counting',
          'Gives the correct number to a group of objects',
          'Counts reliably without skipping or counting an item twice',
        ],
        framework: {
          consultantTier: 'SKIES Associate',
          sources: ['Bayley-4 Cognitive (number sense)', 'ASQ-3 Problem Solving', 'CDC Watch Me!'],
        },
      },
      {
        id: 'math-advanced',
        icon: '📐',
        name: 'Advanced',
        subtitle: 'Mathematics: Level 2',
        price: 159,
        popular: false,
        comingSoon: true,
        color: 'tier-blue',
        includes: [
          '60-minute SKIES consultant session',
          'Full cognitive skills review',
          'Premium certificate (digital + print-ready)',
          'Detailed parent milestone report',
          'Achievement badge + social media graphic',
          'One follow-up progress check call (30 min)',
        ],
        milestones: [
          'Spots simple repeating patterns (for example, red, blue, red, blue)',
          'Predicts what comes next in a repeating pattern',
          'Uses "first," "middle," and "last" correctly for a row of objects',
          'Understands order and position in different arrangements',
        ],
        framework: {
          consultantTier: 'SKIES Practitioner',
          sources: ['Bayley-4 Cognitive (pattern reasoning)', 'ASQ-3 Problem Solving', 'CDC Watch Me!'],
        },
      },
    ],
  },
  {
    id: 'speech-linguistic',
    name: 'Advanced Speech & Language Certificate',
    description:
      "Looks at how well a child understands, retells, and builds language across two progressive levels.",
    tiers: [
      {
        id: 'speech-narrative',
        icon: '📖',
        name: 'Storytelling & Understanding',
        subtitle: 'Speech & Language: Level 1',
        price: 129,
        popular: false,
        color: 'tier-green',
        includes: [
          '45-minute SKIES consultant session',
          'Milestone skills report',
          'Official Stratos Standard certificate (digital + print-ready)',
          'Achievement badge digital download',
        ],
        milestones: [
          'Retells a short story with a clear beginning, middle, and end',
          'Puts the events of a story in the right order on their own',
          'Draws sensible conclusions from a picture (for example, an umbrella and a frown means it is raining and she cannot play outside)',
          'Connects what happens to why it happens',
        ],
        framework: {
          consultantTier: 'SKIES Associate',
          sources: ['Bayley-4 Language (understanding + speaking)', 'ASQ-3 Communication', 'CDC Watch Me!'],
        },
      },
      {
        id: 'speech-phonemic',
        icon: '🗣️',
        name: 'Sounds & Early Reading',
        subtitle: 'Speech & Language: Level 2',
        price: 159,
        popular: false,
        comingSoon: true,
        color: 'tier-blue',
        includes: [
          '60-minute SKIES consultant session',
          'Full language skills review',
          'Premium certificate (digital + print-ready)',
          'Detailed parent milestone report',
          'Achievement badge + social media graphic',
          'One follow-up progress check call (30 min)',
        ],
        milestones: [
          'Recognizes which words rhyme',
          'Names the first sound in a spoken word (for example, "apple" starts with the "a" sound)',
          'Uses words like "because," "so," and "but" to connect ideas',
          'Builds longer sentences that give a reason or show a contrast',
        ],
        framework: {
          consultantTier: 'SKIES Practitioner',
          sources: ['Bayley-4 Language (sound-awareness checks)', 'ASQ-3 Communication', 'CDC Watch Me!'],
        },
      },
    ],
  },
];

const FAQS = [
  {
    q: 'What is a MACH certificate?',
    a: 'MACH stands for Milestone Achieved Certified Holder. These certificates recognize the big firsts in a child\'s earliest years: first words, first steps, color naming, shape recognition, counting to ten, and first sentences. They are usually for children ages 0 to 3. Every MACH a child earns moves their MACH status up (MACH 1, MACH 2, MACH 3, and on) and builds the groundwork for our SKIES foundational certifications.',
  },
  {
    q: 'How does MACH bundle pricing work?',
    a: 'A single MACH certification is $70 for one session. You can bundle multiple MACH certs into the same session for $20 each additional. So two is $90, three is $110, four is $130, and so on. All bundled certs are assessed during the single session.',
  },
  {
    q: 'How is a MACH cert different from a SKIES Foundational?',
    a: 'MACH certificates celebrate single firsts you can see: a child\'s very first sentence, the moment they can name five colors, their first 5 steps on their own. SKIES Foundational certifications are deeper assessments that look at several skills at once, grounded in the Bayley-4 and ASQ-3 frameworks. MACH is the building-block series, and SKIES is the foundational standard.',
  },
  {
    q: 'What is a SKIES session?',
    a: 'A SKIES (Supporting Kids In Early Success) session is a warm, play-based observation conducted by one of our trained consultants. It recognizes children who are meeting or exceeding developmental milestones for their age.',
  },
  {
    q: 'How long does the session take?',
    a: 'Depending on the level, sessions range from 45 to 90 minutes and are held online by appointment.',
  },
  {
    q: 'When will we receive the certificate?',
    a: 'Digital and print-ready certificates are included with every level and sent within 24 hours of the session. Physical mailing is available as an optional add-on: a certificate cover plus mailing is +$50, or a framed certificate plus mailing is +$100. Mailed orders go out within 5 to 7 business days.',
  },
  {
    q: 'What if my child doesn\'t meet the requirements?',
    a: 'Every child is unique. If your child doesn\'t fully meet the criteria, we provide a detailed report with guidance on their development. You can rebook in 60 days at 25% off.',
  },
  {
    q: 'Are sessions virtual?',
    a: 'Yes. All sessions are held online by video call. You\'ll receive a link with your confirmation email after booking.',
  },
];

export default function Services() {
  const [openFaq, setOpenFaq] = React.useState(null);

  return (
    <>
      <Seo
        title="Achievement Programs & Pricing"
        description="Explore Stratos Standard achievement programs for children ages 0–6. Each program is a warm, play-based verification that earns a permanent registry entry — recognition, not assessment."
        path="/services"
      />
      <section className="page-hero">
        <div className="container">
          <p className="eyebrow" style={{ color: '#3ba3e8', position: 'relative' }}>Certification Programs</p>
          <h1 className="page-hero-title">Services &amp; Pricing</h1>
          <p className="page-hero-sub">
            Choose the certificate that matches your child's abilities and let our SKIES consultants do the rest.
          </p>
        </div>
      </section>

      {/* MACH — Milestone Achieved Certified Holder */}
      <section className="section-pad mach-section">
        <div className="container">
          <div className="mach-intro text-center">
            <p className="eyebrow">MACH: Milestone Achieved Certified Holder</p>
            <h2 className="section-title">Celebrate the Firsts</h2>
            <p className="cert-group-desc">
              MACH certificates recognize the big firsts of a child's earliest years.
              These are the milestones that build the groundwork for our SKIES
              certification tracks. Made for the 0 to 3 age range, each MACH a child
              earns moves their MACH status up: MACH 1, MACH 2, MACH 3, and beyond.
            </p>
            <div className="accent-line" />
          </div>

          <div className="mach-bundle-banner">
            <p className="mach-bundle-eyebrow">Bundle &amp; Save</p>
            <p className="mach-bundle-text">
              <strong>${MACH_BASE_PRICE}</strong> for one MACH cert
              <span className="mach-bundle-sep"> · </span>
              <strong>+${MACH_ADDITIONAL_PRICE}</strong> for each additional cert in the same session
            </p>
          </div>

          <div className="mach-grid">
            {MACH_CERTS.map((m) => (
              <div key={m.id} className="mach-card">
                <div className="mach-card-top">
                  <span className="mach-badge">MACH</span>
                  <span className="mach-icon" aria-hidden="true">{m.icon}</span>
                </div>
                <h3 className="mach-name">{m.name}</h3>
                <p className="mach-age">{m.typicalAge}</p>
                <p className="mach-criterion">{m.criterion}</p>
                <div className="mach-meta">
                  <p className="mach-meta-row">
                    <span className="mach-meta-label">Builds toward</span>
                    <span className="mach-meta-value">{m.leadsTo}</span>
                  </p>
                </div>
                <div className="mach-price-row">
                  <span className="mach-price">
                    <span className="mach-price-dollar">$</span>{MACH_BASE_PRICE}
                  </span>
                  <Link to={`/book?service=${m.id}`} className="btn btn-aero btn-sm mach-book-btn">
                    Book Now
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="mach-status-strip">
            <p className="eyebrow">MACH Status</p>
            <p className="mach-status-title">Every Milestone Counts Toward Their Rank</p>
            <div className="mach-status-row">
              {[1, 2, 3, 4, 5, 6].map((rank) => (
                <div key={rank} className="mach-status-pill">
                  <span className="mach-status-num">MACH {rank}</span>
                  <span className="mach-status-label">{rank} cert{rank > 1 ? 's' : ''}</span>
                </div>
              ))}
            </div>
            <p className="mach-status-sub">
              Each MACH cert your child earns advances their official status. By the time they're
              ready for our SKIES foundational tracks, you'll have a wall of milestones
              celebrating the journey there.
            </p>
            <Link to="/book?service=mach-firstwords" className="btn btn-primary btn-lg" style={{ marginTop: '20px' }}>
              Start Your MACH Journey
            </Link>
          </div>
        </div>
      </section>

      {/* Certificate Groups */}
      <section className="section-pad">
        <div className="container">
          <div className="text-center" style={{ marginBottom: '56px' }}>
            <p className="eyebrow">SKIES Foundational Certifications</p>
            <h2 className="section-title">From Firsts to Foundations</h2>
            <p className="cert-group-desc">
              Once your child is ready for deeper assessments, our SKIES consultant tiers
              take over and build on the foundations celebrated by their MACH certifications.
            </p>
            <div className="accent-line" />
          </div>
          {CERTIFICATE_GROUPS.map((group) => (
            <div key={group.id} className="cert-group">
              <div className="cert-group-header text-center">
                <p className="eyebrow">{group.name}</p>
                <p className="cert-group-desc">{group.description}</p>
                <div className="accent-line" />
              </div>

              <div className="tiers-grid">
                {group.tiers.map((tier) => (
                  <div key={tier.id} className={`tier-card ${tier.color} ${tier.popular && !tier.comingSoon ? 'tier-popular' : ''} ${tier.comingSoon ? 'tier-coming-soon' : ''}`}>
                    {tier.popular && !tier.comingSoon && <div className="popular-ribbon">Most Popular</div>}
                    {tier.comingSoon && <div className="coming-soon-banner">Coming Soon</div>}
                    <div className="tier-header">
                      <span className="tier-icon">{tier.icon}</span>
                      <span className="tier-age">{tier.subtitle}</span>
                      <h2 className="tier-name">{tier.name}</h2>
                      <div className="tier-price">
                        <span className="price-dollar">$</span>
                        <span className="price-amt">{tier.price}</span>
                        <span className="price-per">/session</span>
                      </div>
                    </div>

                    <div className="tier-body">
                      <p className="tier-section-label">What's Included</p>
                      <ul className="tier-includes">
                        {tier.includes.map((item, i) => (
                          <li key={i}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d4a017" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                            {item}
                          </li>
                        ))}
                      </ul>

                      <p className="tier-section-label" style={{ marginTop: '20px' }}>Skills Demonstrated</p>
                      <ul className="tier-milestones">
                        {tier.milestones.map((m, i) => (
                          <li key={i}>
                            <span className="ms-dot" />
                            {m}
                          </li>
                        ))}
                      </ul>

                      {tier.framework && (
                        <div className="tier-framework">
                          <p className="tier-section-label" style={{ marginTop: '20px' }}>Assessment Framework</p>
                          <p className="tier-framework-row">
                            <span className="tier-framework-label">Delivered by</span>
                            <span className="tier-framework-value">{tier.framework.consultantTier}</span>
                          </p>
                          <p className="tier-framework-row">
                            <span className="tier-framework-label">Grounded in</span>
                            <span className="tier-framework-value">{tier.framework.sources.join(' · ')}</span>
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="tier-footer">
                      {tier.comingSoon ? (
                        <button
                          type="button"
                          className="btn btn-lg btn-disabled"
                          disabled
                          style={{ width: '100%', justifyContent: 'center' }}
                        >
                          Coming Soon
                        </button>
                      ) : (
                        <Link
                          to={`/book?service=${tier.id}`}
                          className={`btn btn-lg ${tier.popular ? 'btn-primary' : 'btn-aero'}`}
                          style={{ width: '100%', justifyContent: 'center' }}
                        >
                          Book {tier.name} Level
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* What to expect */}
      <section className="section-pad what-to-expect">
        <div className="container text-center">
          <p className="eyebrow">The Experience</p>
          <h2 className="section-title">What to Expect</h2>
          <div className="accent-line" />
          <div className="expect-grid" style={{ marginTop: '48px' }}>
            {[
              { icon: '😊', title: 'Warm & Play-Based', desc: 'Our consultants are trained to make children feel at ease. Sessions are fun, engaging, and stress-free.' },
              { icon: '📋', title: 'Structured Session', desc: 'Each session follows the SKIES framework, which is rigorous, fair, and right for your child\'s age.' },
              { icon: '🏆', title: 'Prestigious Certification', desc: 'Our certificates are beautiful, meaningful, and recognized as a genuine mark of early achievement.' },
              { icon: '💌', title: 'Parent Partnership', desc: "We send you a detailed report so you can continue celebrating and supporting your child's growth." },
            ].map((item, i) => (
              <div key={i} className="expect-card">
                <span className="expect-icon">{item.icon}</span>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-pad faq-section">
        <div className="container">
          <div className="text-center" style={{ marginBottom: '48px' }}>
            <p className="eyebrow">Got Questions?</p>
            <h2 className="section-title">Frequently Asked Questions</h2>
          </div>
          <div className="faq-list">
            {FAQS.map((faq, i) => (
              <div key={i} className={`faq-item ${openFaq === i ? 'open' : ''}`}>
                <button className="faq-question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {faq.q}
                  <svg className="faq-chevron" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                </button>
                <div className="faq-answer">
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad text-center" style={{ background: '#f8f9fc' }}>
        <div className="container">
          <p className="eyebrow">Ready?</p>
          <h2 className="section-title">Book Your Session Today</h2>
          <p className="section-subtitle">Your child's achievement deserves to be seen. Let's make it official.</p>
          <Link to="/book" className="btn btn-primary btn-lg">Schedule Now</Link>
        </div>
      </section>
    </>
  );
}
